import { Plate } from 'metalworks-domain'

import { configuratorInterface } from '../configurator-interface'

export const powderCoatingMaterialTransformer = configuratorInterface.transformerBuilder.when
  .entity(Plate.types)
  .violated(['powderCoatingAvailableForMaterial'])
  .then.applySideEffect(ctx => {
    const oldPlate = ctx.getOld$Root()
    const $plate = ctx.getNew$Root()
    const powderCoatingWasEnabled = !oldPlate.powderCoating && $plate.powderCoating
    const rawMaterialWasChanged = oldPlate.rawMaterial !== $plate.rawMaterial

    if (!powderCoatingWasEnabled && !rawMaterialWasChanged) {
      return []
    }

    const supportedMaterials = $plate.$customMaterials.filter(
      material => material.rawMaterial === $plate.rawMaterial && material.powderCoatingEnabled
    )
    const closestSupportedMaterial = supportedMaterials.reduce<(typeof supportedMaterials)[number] | undefined>(
      (closest, material) =>
        !closest || Math.abs(material.thickness - $plate.thickness) < Math.abs(closest.thickness - $plate.thickness)
          ? material
          : closest,
      undefined
    )
    const supportedMaterial =
      supportedMaterials.find(material => material.thickness === $plate.thickness) ?? closestSupportedMaterial

    if (!supportedMaterial) {
      return []
    }

    const materialWasChanged = rawMaterialWasChanged || supportedMaterial.finish !== $plate.finish
    const thicknessWasChanged = supportedMaterial.thickness !== $plate.thickness
    const hint =
      materialWasChanged && thicknessWasChanged
        ? 'Material changed to milled and a thicker plate was selected for powder-coating manufacturing. The visual appearance is unchanged.'
        : thicknessWasChanged
          ? 'A thicker plate was selected to meet powder-coating manufacturing requirements.'
          : 'Material changed to milled. Powder coating requires a milled plate. The visual appearance is unchanged.'

    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: $plate.id,
        updateTag: 'fix.powderCoatingMaterial',
        stateDelta: {
          rawMaterial: supportedMaterial.rawMaterial,
          finish: supportedMaterial.finish,
          thickness: supportedMaterial.thickness,
        },
        hint,
      }),
    ]
  })
  .build('Use a powder-coating-enabled material')

export const powderCoatingRemoveMountingHoleTransformer = configuratorInterface.transformerBuilder.when
  .entity(Plate.types)
  // For some weird reason this did no work with:
  //   .violated(['powderCoatingHoleNotDeleted'])
  .modified({ atFields: ['powderCoating'] })
  .then.applySideEffect(ctx => {
    const $plate = ctx.getNew$Root()

    if (!$plate.powderCoating && $plate.$powderCoatingMountingHole) {
      return [
        configuratorInterface.updates.DomainEntityModification({
          entityId: $plate.id,
          updateTag: 'fix.unmarkPowderCoatingHoleAsMountingHole',
          stateDelta: { powderCoatingMountingHoleId: null },
        }),
        configuratorInterface.updates.EntityDeletion({
          entityId: $plate.$powderCoatingMountingHole.id,
          updateTag: 'fix.deletePowderCoatingHole',
        }),
        ...$plate.$powderCoatingMountingHole.$anchorPoints.map(ap =>
          configuratorInterface.updates.EntityDeletion({
            entityId: ap.id,
            updateTag: 'fix.deletePowderCoatingHole',
          })
        ),
      ]
    }

    return []
  })
  .build('Remove automatically added mounting hole for powder coating')
